<template>
    <div class="alert-notifier">

        <!-- Link w stopce -->
        <Button v-if="alerts.length > 0"
                @click="showModal = true"
                :badge="alerts.length.toString()"
                :pt="alertButtonPt"
                style="color:red;font-size:20px"
                badgeSeverity="contrast"
                variant="outlined"
                icon="pi pi-bell"
                severity="warning"
                text
                rounded
                aria-label="Alerty"
                v-tooltip.bottom="__('Notifications')"
                :unstyled="true" />

        <!-- Modal z listą alertów -->
        <Dialog v-model:visible="showModal"
                :header="__('Active alerts')"
                :modal="true"
                :style="{ width: '70%' }"
                :unstyled="false"
                class="form-modal"
                :pt="dialogPt">
            <div v-if="alerts.length === 0"
                 class="text-center p-4">
                {{ __('No active alerts') }}
            </div>

            <div v-else
                 class="flex flex-col gap-3">
                <div v-for="alert in alerts"
                     :key="alert.id"
                     class="p-3 border rounded-lg">
                    <div class="flex justify-between items-start mb-2">
                        <Tag :value="alert.type"
                             severity="info" />
                        <div class="flex flex-col items-end">
                            <span class="text-sm text-gray-500">{{ alert.show_alert_date }}</span>
                            <span class="text-xs text-gray-300">{{ alert.creator }}</span>
                        </div>
                    </div>

                    <p class="mb-3 alert-message"
                       v-html="alert.message"></p>

                    <div class="flex gap-2">
                        <Button :label="__('Read')"
                                icon="pi pi-check"
                                @click="markAsRead(alert)"
                                :unstyled="true"
                                :pt="actionButtonPt('secondary')" />

                        <Button :label="__('Postpone')"
                                icon="pi pi-clock"
                                @click="openPostponeDialog(alert)"
                                :unstyled="true"
                                :pt="actionButtonPt('secondary')" />

                        <Button v-if="alert.source_table && alert.source_id"
                                :label="__('Go to source')"
                                icon="pi pi-external-link"
                                @click="goToSource(alert)"
                                :unstyled="true"
                                :pt="actionButtonPt('secondary')" />
                    </div>
                    <div v-if="errorMsg"
                         class="text-red-500 mt-2">{{ errorMsg }}</div>
                </div>
            </div>
        </Dialog>

        <!-- Dialog odłożenia alertu -->
        <Dialog modal
                v-model:visible="showPostponeDialog"
                :header="__('Postpone alert')"
                :unstyled="false"
                :style="{ width: '400px' }"
                class="delete-confirmation-modal">
            <div class="flex flex-col gap-3">
                <FormCalendar fieldName="postpone_alert_date"
                              label="New date of alert"
                              showTime
                              hourFormat="24"
                              dateFormat="yy-mm-dd"
                              :minDate="new Date()"
                              :showIcon="true"
                              v-model="postponeDate" />

                <div class="flex justify-end gap-2 mt-5 confirmation-actions">
                    <Button :label="__('Cancel')"
                            @click="showPostponeDialog = false"
                            :unstyled="true"
                            :pt="actionButtonPt('secondary')" />
                    <Button :label="__('Save')"
                            @click="postponeAlert"
                            :unstyled="true"
                            :pt="actionButtonPt('primary')" />
                </div>
                <div v-if="errorMsg"
                     class="text-red-500 mt-2">{{ errorMsg }}</div>
            </div>
        </Dialog>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { usePage, router } from '@inertiajs/vue3';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Tag from 'primevue/tag';
import FormCalendar from '@crud/FormCalendar.vue';
import DatePicker from 'primevue/datepicker';
import { __, getFormattedDateTime, getFromRoute, singularize, screenMessage } from '@helper/utils.js';
import notificationSound from '@sounds/notification.mp3';

const dialogPt = {
    header: { class: 'form-header' },
    content: { class: 'form-body' },
};

const alertButtonPt = {
    root: {
        class:
            'alert-button relative rounded-full p-1 text-gray-400 hover:text-white focus:outline-2 focus:outline-offset-2 focus:outline-indigo-500',
    },
    badge: { class: 'alert-icon-badge' },
};

const actionButtonPt = (variant) => {
    const base =
        'inline-flex items-center gap-2 bg-white font-medium py-1 px-3 rounded-md border transition-colors duration-150 select-none';

    const variants = {
        primary: 'hover:bg-secondary-50 text-secondary-700 border-secondary-200 hover:border-secondary-300',
        success: 'hover:bg-emerald-50 text-emerald-700 border-emerald-200 hover:border-emerald-300',
        info: 'hover:bg-sky-50 text-sky-700 border-sky-200 hover:border-sky-300',
        secondary: 'hover:bg-gray-200 text-gray-600 border-gray-200 hover:border-gray-300',
    };

    return {
        root: { class: `${base} ${variants[variant] ?? variants.secondary}` },
        icon: { class: 'text-xs' },
    };
};

const page = usePage();
const alerts = ref([]);
const showModal = ref(false);
const showPostponeDialog = ref(false);
const postponeDate = ref(null);
const selectedAlert = ref(null);
const errorMsg = ref(null);
const pollingInSeconds = 60;
let pollingInterval = null;

// Mapowanie tabel na routy tylko dla niestandardowych nazw tabel
const sourceRoutes = {
    'opportunities': 'opportunity-index',
};

// ------------------------------------------------------------------------------------------------------------

onMounted(() => {
    const notificationSystem = page.props.notificationSystem || 'polling';
    if (notificationSystem === 'polling') {
        startPolling();
    } else if (notificationSystem === 'websocket') {
        subscribeToAlerts();
    }
    window.addEventListener('refresh-alerts-list', fetchAlerts);
});

// ------------------------------------------------------------------------------------------------------------

onUnmounted(() => {
    const userId = page.props.userId;
    const notificationSystem = page.props.notificationSystem || 'polling';

    window.removeEventListener('refresh-alerts-list', fetchAlerts);
    if (notificationSystem === 'polling') {
        stopPolling();
    } else if (userId) {
        window.Echo.leave(`alerts.${userId}`);
    }
});

// ------------------------------------------------------------------------------------------------------------

// Pobierz aktywne alerty przy starcie
async function fetchAlerts() {
    try {
        const previousCount = alerts.value.length;
        alerts.value = await getFromRoute('alert-active')

        if (alerts.value.length > previousCount) {
            playNotificationSound();
        }
    } catch (error) {
        console.error(__('Error occured fetching alerts') + ': ', error);
    }
};

// ------------------------------------------------------------------------------------------------------------

// Obowiązuje tylko dla konfiguracji z WebSocket
// Nasłuchuj na WebSocket
function subscribeToAlerts() {
    const userId = page.props.userId;
    if (!userId) return;

    fetchAlerts();
    window.Echo.private(`alerts.${userId}`)
        .listen('.alert.show', (event) => {
            // Dodaj nowy alert do listy (jeśli nie istnieje)
            const exists = alerts.value.some(a => a.id === event.alertData.id);
            if (!exists) {
                alerts.value.unshift(event.alertData);
                playNotificationSound();
            }
        });
};

// ------------------------------------------------------------------------------------------------------------

// Obowiązuje tylko dla konfiguracji z pollingiem
// Uruchom polling co pollingInSeconds sekund
function startPolling() {
    const userId = page.props.userId;
    if (!userId) return;

    fetchAlerts();
    pollingInterval = setInterval(() => {
        fetchAlerts();
    }, pollingInSeconds * 1000);
};

// ------------------------------------------------------------------------------------------------------------

// Zatrzymaj polling
function stopPolling() {
    if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
    }
};

// ------------------------------------------------------------------------------------------------------------

// Oznacz jako przeczytany
async function markAsRead(alert) {
    try {
        errorMsg.value = null;
        await getFromRoute('alert-read', { id: alert.id }, 'post');

        alerts.value = alerts.value.filter(a => a.id !== alert.id);
        if (alerts.value.length === 0) {
            showModal.value = false;
        }
    } catch (error) {
        errorMsg.value = error.response?.data?.message || __('Error occured marking alert as read');
        console.error(__('Error occured marking alert as read') | ': ', error);
    }
};

// ------------------------------------------------------------------------------------------------------------

// Otwórz dialog odłożenia
function openPostponeDialog(alert) {
    errorMsg.value = null;
    selectedAlert.value = alert;
    postponeDate.value = new Date(Date.now() + 3600000); // Domyślnie +1h
    showPostponeDialog.value = true;
};

// ------------------------------------------------------------------------------------------------------------

// Odłóż alert
async function postponeAlert() {
    if (!selectedAlert.value || !postponeDate.value) return;

    try {
        errorMsg.value = null;
        const formattedDateTime = getFormattedDateTime(postponeDate.value);

        await getFromRoute('alert-postpone', { id: selectedAlert.value.id, show_alert_date: formattedDateTime }, 'post');

        alerts.value = alerts.value.filter(a => a.id !== selectedAlert.value.id);
        showPostponeDialog.value = false;

        if (alerts.value.length === 0) {
            showModal.value = false;
        }
    } catch (error) {
        errorMsg.value = error.response?.data?.message || __('Error occured during postpone alert');
    }
};

// ------------------------------------------------------------------------------------------------------------

// Przejdź do źródła alertu
function goToSource(alert) {
    let routeName = sourceRoutes[alert.source_table];
    if (!routeName) {
        routeName = singularize(alert.source_table) + '-index';
    }
    if (!routeName) {
        console.warn('Nie znaleziono route dla tabeli źródłowej:', alert.source_table);
        return;
    }
    if (routeName) {
        console.log('Nawigacja do źródła alertu:', routeName, 'ID:', alert.source_id);
        try {
            router.visit(route(routeName, { id: alert.source_id }));
        } catch (error) {
            const $msg = __('Error during navigate to alert source') + ' (' + routeName + ', id:' + alert.source_id + ')';
            screenMessage({ message: $msg, title: "Error", type: "info", position: "center", timeout: 5 });
            console.error(__('Error during navigate to alert source'), error);
        }
        //router.visit(route(routeName, { id: alert.source_id }));
    }
};

// ------------------------------------------------------------------------------------------------------------

//Odtwórz dźwięk powiadomienia
function playNotificationSound() {
    const playSounds = page.props.playSounds || false;
    if (!playSounds) return;

    const audio = new Audio(notificationSound); // Ścieżka do pliku w public/sounds/
    audio.play().catch(err => {
        console.warn('Nie udało się odtworzyć dźwięku:', err);
    });
}

// ------------------------------------------------------------------------------------------------------------

</script>

<style scoped>
.alert-notifier {
    display: inline-flex;
    margin-right: 20px;
}

.alert-message {
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
    white-space: normal;
    max-width: 100%;
}

:deep(.alert-icon-badge) {
    position: absolute;
    top: -2px;
    right: -2px;

    min-width: 18px;
    height: 18px;
    padding: 0 5px;

    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-size: 8px;
    font-weight: 700;
    line-height: 1;
    box-shadow: 0 0 0 2px rgba(31, 41, 55, 1);
}

:deep(.alert-button) {
    background-color: transparent;
    box-sizing: border-box;
    box-shadow: none !important;
}

:deep(.alert-button):hover {
    background-color: rgba(0, 0, 0, 0.15) !important;
}
</style>
